import { X } from "lucide-react";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import { Dialog } from "radix-ui";

import { Button } from "@/design-system/components/Button/Button";
import { Badge } from "@/design-system/components/Badge/Badge";
import { LoadedDestinationInfo } from "@/types/global";
import { makeRequest } from "@/util";
import { destinationConfigPayload } from "@/destinations/utils/destinationConfigPayload";
import { LoadingSpinner } from "@/design-system/components/LoadingSpinner/LoadingSpinner";
import { unpackRequestErrorMessage } from "@/utils/request";
import { useUserHasAccess } from "@/hooks/useUserHasAccess";
import { RBACAccessLevel, RBACDomain } from "@/access-control/types";
import Unauthorized from "@/components/users/Unauthorized";
import { Dialog as FPDialog } from "@/design-system/components/Dialog/Dialog";
import { Accordion } from "@/design-system/components/Accordion/Accordion";
import useListAllDestinationsForEnv from "@/destinations/v2/hooks/useListAllDestinationsForEnv";

interface IdentityMatchingConfirmationModalProps {
  open: boolean;
  close: () => void;
  destination: LoadedDestinationInfo;
}

export function IdentityResolutionConfigModal({
  open,
  close,
  destination,
}: IdentityMatchingConfirmationModalProps) {
  const { formState, handleSubmit, setError } = useForm();
  const { refetch: refetchDestinations } = useListAllDestinationsForEnv();

  const hasAllowlistWriteAccess = useUserHasAccess(
    RBACDomain.allowlist,
    RBACAccessLevel.write,
  );

  const enabled = !!destination.settings.identityMatchingEnabled;

  const copy = getCopy(enabled);

  const onSubmit = useCallback(async () => {
    try {
      await toggleIdentityResolutionConfig(destination);
      refetchDestinations();
      close();
    } catch (error) {
      setError("root", {
        message: unpackRequestErrorMessage(error),
      });
    }
  }, [close, destination, refetchDestinations, setError]);

  const requestError = formState.errors.root;

  if (open && !hasAllowlistWriteAccess) {
    return (
      <FPDialog title="Unauthorized" description="" onOpenChange={close} open>
        <Unauthorized
          containerStyle={{ paddingTop: 0 }}
          message="Ask an admin to grant permission to edit allowlists"
        />
      </FPDialog>
    );
  }

  return (
    <Dialog.Root open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className="data-[state=open]:animate-fadeIn data-[state=closed]:animate-fadeOut fixed top-0 left-0 flex h-full w-full flex-col items-center justify-center bg-black/50">
          <Dialog.Content
            onEscapeKeyDown={close}
            onPointerDownOutside={close}
            className="w-160 rounded-md bg-white shadow dark:bg-gray-100"
          >
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="flex items-center justify-between border-b px-6 py-4">
                <Dialog.Title className="text-textDefault text-lg font-semibold">
                  Configure identity matching
                </Dialog.Title>
                <Dialog.Close
                  onClick={close}
                  className="text-iconDefault cursor-pointer hover:text-gray-600"
                >
                  <X />
                </Dialog.Close>
              </div>

              <div className="p-6">
                <div className="bg-surface-minimal flex items-start justify-between rounded-md p-4">
                  <div className="space-y-1">
                    <h5 className="font-bold">{copy.title}</h5>
                    <p className="text-sm">{copy.subtitle}</p>
                  </div>

                  <Badge
                    text={copy.indicator}
                    variant={enabled ? "success" : "unknown"}
                  />
                </div>

                <Accordion className="mt-4" type="single" collapsible>
                  <Accordion.Item
                    value="details"
                    triggerPosition="bottom"
                    showMore="How does this work?"
                  >
                    <div className="text-textSubtle mt-2 space-y-4 text-sm">
                      <p>
                        When you enable Identity Matching you can match and
                        resolve your anonymous web traffic collected by
                        Freshpaint to email addresses using a licensed
                        third-party identity graph.
                      </p>
                      <p>
                        Identity Matching relies on probabilistic resolution,
                        which may result in a matched audience size being
                        different from the original audience size. Your
                        configuration of Identity Matching enables you to
                        support HIPAA compliant identity resolution.
                      </p>
                      <p>
                        <strong>
                          All data and matching occur within the Freshpaint
                          environment, and no data is shared externally or used
                          to build the identity graph itself.
                        </strong>
                      </p>
                      <p>
                        See the{" "}
                        <a
                          href="https://documentation.freshpaint.io"
                          rel="noopener noreferrer"
                          target="_blank"
                          className="text-textActionSelected underline"
                        >
                          Freshpaint Documentation
                        </a>{" "}
                        for more details on how Identity Matching works and how
                        to configure identity resolution to support HIPAA
                        compliance.
                      </p>
                    </div>
                  </Accordion.Item>
                </Accordion>

                <div className="flex items-center justify-end gap-4 pt-8">
                  <span className="text-textError mr-auto">
                    {requestError?.message}
                  </span>
                  <Button
                    onClick={close}
                    variant="link"
                    className="text-textSubtle"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      !hasAllowlistWriteAccess || formState.isSubmitting
                    }
                    leadingIcon={
                      formState.isSubmitting ? (
                        <LoadingSpinner className="size-4" />
                      ) : null
                    }
                  >
                    {copy.button}
                  </Button>
                </div>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function getCopy(enabled: boolean) {
  if (enabled) {
    return {
      title: "Identity matching is currently enabled",
      subtitle:
        "You can send segmented audiences to ad platforms that require email",
      indicator: "Enabled",
      button: "Disable",
    };
  }
  return {
    title: "Identity Matching is currently disabled",
    subtitle:
      "You cannot send segmented audiences to ad platforms that require email. Click enable to send anonymous web traffic to ad platforms.",
    indicator: "Disabled",
    button: "Enable",
  };
}

async function toggleIdentityResolutionConfig(
  destinationConfig: LoadedDestinationInfo,
) {
  const nextState = !destinationConfig.settings.identityMatchingEnabled;

  await makeRequest(
    "save_destination_config",
    destinationConfigPayload(destinationConfig, {
      settings: {
        identityMatchingEnabled: nextState,
      },
    }),
  );
}
